import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Calculator } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import heroBg800 from "@/assets/hero-restaurant-bg-800.webp";
import heroBg1280 from "@/assets/hero-restaurant-bg-1280.webp";
import heroBg1920 from "@/assets/hero-restaurant-bg-1920.webp";

const SLIDE_INTERVAL = 10000;

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const Hero = () => {
  const { t } = useTranslation();
  const slides = [
    {
      key: "s0",
      title1: t("hero.slide0_title_1"),
      highlight: t("hero.slide0_title_highlight"),
      title2: t("hero.slide0_title_2"),
      subtitle: t("hero.slide0_subtitle"),
    },
    {
      key: "s1",
      title1: t("hero.title_1"),
      highlight: t("hero.title_highlight"),
      title2: t("hero.title_2"),
      subtitle: t("hero.subtitle"),
    },
    {
      key: "s3",
      title1: t("hero.slide3_title_1"),
      highlight: t("hero.slide3_title_highlight"),
      title2: t("hero.slide3_title_2"),
      subtitle: t("hero.slide3_subtitle"),
    },
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      SLIDE_INTERVAL,
    );
    return () => window.clearInterval(id);
  }, [slides.length]);

  const current = slides[index];

  return (
    <section className="overflow-hidden">
      <div className="relative pt-24 pb-12 md:pt-40 md:pb-24">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBg1280}
            srcSet={`${heroBg800} 800w, ${heroBg1280} 1280w, ${heroBg1920} 1920w`}
            sizes="100vw"
            alt={t("hero.alt_bg")}
            className="absolute inset-0 w-full h-full object-cover object-center"
            width={1920}
            height={1080}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="sr-only">{t("hero.h1")}</h1>
            <AnimatePresence mode="wait">
              <motion.div
                key={current.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="min-h-[220px] md:min-h-[300px] flex flex-col justify-center"
              >
                <p className="text-fluid-display font-bold mb-6 text-white text-balance max-w-[20ch] mx-auto" aria-hidden="true">
                  {current.title1}{" "}
                  <span className="text-gradient">{current.highlight}</span>
                  {current.title2 ? <> {current.title2}</> : null}
                </p>
                <p className="text-fluid-lead text-white/85 mb-8 max-w-[52ch] mx-auto text-pretty line-clamp-4 sm:line-clamp-none">
                  {current.subtitle}
                </p>
              </motion.div>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2 sm:px-0"
            >
              <Button
                size="lg"
                className="shadow-brand text-base px-6 sm:px-8 w-full sm:w-auto"
                onClick={() => scrollTo("pricing")}
              >
                {t("hero.cta_plans")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="text-base px-6 sm:px-8 w-full sm:w-auto border-white bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 hover:text-white"
                onClick={() => scrollTo("pricing")}
              >
                <Calculator className="w-4 h-4 mr-2" />
                {t("hero.cta_calc")}
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="text-xs sm:text-sm text-white/70 mt-5 sm:mt-6 px-4"
            >
              {t("hero.trust")}
            </motion.p>

            <div className="flex justify-center gap-2 mt-6 sm:mt-8">
              {slides.map((s, i) => (
                <button
                  key={s.key}
                  type="button"
                  aria-label={`Slide ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={cn(
                    "h-1.5 rounded-full transition-all",
                    i === index ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/60",
                  )}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
